import { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Copy, Download } from 'lucide-react';
import { toast } from '../../lib/toast.js';

type SetupStep = 'idle' | 'qr' | 'verify' | 'backup';

export default function MfaSettings() {
  const { t } = useTranslation('settings');
  const { user } = useUser();

  const [step, setStep] = useState<SetupStep>('idle');
  const [qrUri, setQrUri] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [confirmedSaved, setConfirmedSaved] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const mfaEnabled = (user?.totpEnabled ?? false) || (user?.twoFactorEnabled ?? false);

  const startSetup = async () => {
    if (!user) return;
    setIsBusy(true);
    try {
      const totp = await user.createTOTP();
      setQrUri(totp.uri ?? '');
      setSecret(totp.secret ?? '');
      setStep('qr');
    } catch {
      toast.error(t('mfa_setup.setup_error'));
    } finally {
      setIsBusy(false);
    }
  };

  const verifyCode = async () => {
    if (!user || !code) return;
    setVerifyError('');
    setIsBusy(true);
    try {
      const totp = await user.verifyTOTP({ code });
      setBackupCodes((totp as { backupCodes?: string[] }).backupCodes ?? []);
      setCode('');
      setStep('backup');
    } catch {
      setVerifyError(t('mfa_setup.verify_error'));
    } finally {
      setIsBusy(false);
    }
  };

  const disableMfa = async () => {
    if (!user) return;
    if (!confirm('Disable two-factor authentication?')) return;
    setIsBusy(true);
    try {
      await user.disableTOTP();
      setStep('idle');
      toast.success('MFA disabled');
    } catch {
      toast.error('Failed to disable MFA');
    } finally {
      setIsBusy(false);
    }
  };

  const copyBackupCodes = () => {
    void navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup codes copied');
  };

  const downloadBackupCodes = () => {
    const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mayksel-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-normal)', marginBottom: 'var(--space-1)' }}>
          {t('mfa_setup.title')}
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
          {t('mfa_setup.subtitle')}
        </p>
      </div>

      <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        <div style={{ padding: 'var(--space-4)', borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-subtle)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <ShieldCheck size={16} style={{ color: mfaEnabled ? 'var(--color-success)' : 'var(--color-text-muted)' }} />
          <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
            {mfaEnabled ? t('mfa_setup.status_enabled') : t('mfa_setup.status_disabled')}
          </p>
        </div>

        <div style={{ padding: 'var(--space-6)' }}>
          {/* Idle state */}
          {step === 'idle' && !mfaEnabled && (
            <button
              type="button"
              onClick={() => { void startSetup(); }}
              disabled={isBusy}
              style={{ padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--color-brand)', color: 'white', fontSize: 'var(--text-sm)', cursor: 'pointer', opacity: isBusy ? 0.6 : 1 }}
            >
              {t('mfa_setup.setup_button')}
            </button>
          )}

          {step === 'idle' && mfaEnabled && (
            <button
              type="button"
              onClick={() => { void disableMfa(); }}
              disabled={isBusy}
              style={{ padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-danger)', background: 'transparent', color: 'var(--color-danger)', fontSize: 'var(--text-sm)', cursor: 'pointer', opacity: isBusy ? 0.6 : 1 }}
            >
              {t('mfa_setup.disable_button')}
            </button>
          )}

          {/* QR code step */}
          {step === 'qr' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', maxWidth: 400 }}>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                {t('mfa_setup.step_scan')}
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-4)', background: 'white', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', width: 'fit-content' }}>
                {qrUri && <QRCodeSVG value={qrUri} size={200} level="M" />}
              </div>
              {secret && (
                <div>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-1)' }}>
                    {t('mfa_setup.step_manual')}
                  </p>
                  <code style={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)', background: 'var(--color-bg-subtle)', padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)', display: 'block', wordBreak: 'break-all' }}>
                    {secret}
                  </code>
                </div>
              )}
              <div>
                <p style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)' }}>{t('mfa_setup.step_verify')}</p>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder={t('mfa_setup.code_placeholder')}
                    style={{ width: 120, padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)', background: 'var(--color-bg)', color: 'var(--color-text)' }}
                  />
                  <button
                    type="button"
                    onClick={() => { void verifyCode(); }}
                    disabled={code.length !== 6 || isBusy}
                    style={{ padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--color-brand)', color: 'white', fontSize: 'var(--text-sm)', cursor: 'pointer', opacity: (code.length !== 6 || isBusy) ? 0.6 : 1 }}
                  >
                    {isBusy ? t('mfa_setup.verifying') : t('mfa_setup.verify_button')}
                  </button>
                </div>
                {verifyError && (
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-danger)', marginTop: 'var(--space-1)' }}>{verifyError}</p>
                )}
              </div>
            </div>
          )}

          {/* Backup codes step */}
          {step === 'backup' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', maxWidth: 480 }}>
              <div>
                <h2 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)', marginBottom: 'var(--space-2)' }}>
                  {t('mfa_setup.backup_title')}
                </h2>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-warning)', background: 'var(--color-warning-pale)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)' }}>
                  ⚠ {t('mfa_setup.backup_warning')}
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                {backupCodes.map((c, i) => (
                  <code key={i} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', background: 'var(--color-bg-subtle)', padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)' }}>
                    {c}
                  </code>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <button type="button" onClick={copyBackupCodes} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', background: 'var(--color-bg)', fontSize: 'var(--text-sm)', cursor: 'pointer', color: 'var(--color-text)' }}>
                  <Copy size={14} />{t('mfa_setup.backup_copy')}
                </button>
                <button type="button" onClick={downloadBackupCodes} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', background: 'var(--color-bg)', fontSize: 'var(--text-sm)', cursor: 'pointer', color: 'var(--color-text)' }}>
                  <Download size={14} />{t('mfa_setup.backup_download')}
                </button>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer', fontSize: 'var(--text-sm)' }}>
                <input type="checkbox" checked={confirmedSaved} onChange={e => setConfirmedSaved(e.target.checked)} />
                {t('mfa_setup.backup_confirm')}
              </label>

              <button
                type="button"
                disabled={!confirmedSaved}
                onClick={() => setStep('idle')}
                style={{ alignSelf: 'flex-start', padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--color-brand)', color: 'white', fontSize: 'var(--text-sm)', cursor: 'pointer', opacity: confirmedSaved ? 1 : 0.5 }}
              >
                {t('mfa_setup.done_button')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
